import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, resolveComponent as _resolveComponent, withCtx as _withCtx, withModifiers as _withModifiers, createElementVNode as _createElementVNode } from "vue"

import { computed, ref } from "vue";
import {
  ArtifactDeltaState,
  FlatArtifact,
  TableGroupRow,
  TraceCountTypes,
} from "@/types";
import {
  deltaTypeOptions,
  artifactAttributesColumns,
  artifactColumns,
  artifactDeltaColumn,
  actionsColumn,
  traceCountOptions,
} from "@/util";
import {
  appStore,
  artifactStore,
  attributesStore,
  deltaStore,
  documentStore,
  selectionStore,
  subtreeStore,
  timStore,
} from "@/hooks";
import {
  PanelCard,
  GroupableTable,
  AttributeChip,
  MultiselectInput,
  SelectInput,
} from "@/components/common";
import {
  ArtifactContentDisplay,
  ArtifactNameDisplay,
} from "@/components/artifact/display";
import TextButton from "@/components/common/button/TextButton.vue";
import ArtifactTableRowActions from "./ArtifactTableRowActions.vue";


/**
 Represents a table of artifacts.
 */
const __default__ = {
  name: "ArtifactTable",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const customCells: (keyof FlatArtifact | string)[] = [
  "name",
  "type",
  "deltaType",
  "actions",
];

const deltaOptions = deltaTypeOptions();
const countOptions = traceCountOptions();

const groupBy = ref<string | undefined>("type");
const visibleTypes = ref<string[] | null>([]);
const countType = ref<TraceCountTypes>("all");
const deltaTypes = ref<ArtifactDeltaState[] | null>([]);

const loading = computed(() => appStore.isLoading > 0);
const inDeltaView = computed(() => deltaStore.inDeltaView);
const typeOptions = computed(() => timStore.typeNames);

const expanded = ref<string[]>([]);
const showAll = ref(false);

const columns = computed(() => [
  ...artifactColumns,
  ...(inDeltaView.value ? [artifactDeltaColumn] : []),
  ...artifactAttributesColumns(attributesStore.attributes),
  actionsColumn,
]);

const rows = computed(() => artifactStore.getFlatArtifacts(showAll.value));

/**
 * Returns props for a mode button.
 * @param mode - The mode button to get props for.
 */
function buttonProps(mode: "all" | "view") {
  const selected = mode === "all" ? showAll.value : !showAll.value;

  return {
    text: !selected,
    outlined: selected,
    color: "text",
  };
}

/**
 * Filters out rows that don't match the selected delta types.
 * @param row - The artifact to filter.
 * @return Whether to keep the row.
 */
function filterRow(row: FlatArtifact): boolean {
  const subtree = subtreeStore.getSubtreeItem(row.id);
  const visible = visibleTypes.value || [];
  const delta = deltaTypes.value || [];

  return (
    ((visible.length || 0) === 0 || visible.includes(row.type)) &&
    (!inDeltaView.value ||
      delta.length === 0 ||
      delta.includes(getDeltaType(row))) &&
    (countType.value === "all" ||
      (countType.value === "onlyTraced" && subtree.neighbors.length > 0) ||
      (countType.value === "notTraced" && subtree.neighbors.length === 0))
  );
}

/**
 * Returns the delta type for a row.
 * @param row - The artifact to check.
 * @return The type of change delta loaded for this artifact.
 */
function getDeltaType(row: FlatArtifact): ArtifactDeltaState {
  return deltaStore.getArtifactDeltaType(row.id);
}

/**
 * Opens the view artifact side panel.
 * @param row - The artifact to view.
 */
function handleView(row: TableGroupRow | FlatArtifact) {
  if ("id" in row) {
    selectionStore.toggleSelectArtifact(String(row.id));
  }
}

/**
 * Expands all panels in the group.
 * @param groupBy - The grouped field.
 * @param groupValue - The grouped value.
 */
function handleOpenGroup(groupBy: keyof FlatArtifact, groupValue: unknown) {
  expanded.value = rows.value
    .filter((row) => row[groupBy] === groupValue)
    .map((row) => row.id);
}

/**
 * Collapses all panels in the group.
 * @param groupBy - The grouped field.
 * @param groupValue - The grouped value.
 */
function handleCloseGroup(groupBy: keyof FlatArtifact, groupValue: unknown) {
  expanded.value = rows.value
    .filter(
      (row) => expanded.value.includes(row.id) && row[groupBy] !== groupValue
    )
    .map((row) => row.id);
}

return (_ctx: any,_cache: any) => {
  const _component_q_btn_group = _resolveComponent("q-btn-group")!

  return (_openBlock(), _createBlock(_unref(PanelCard), null, {
    default: _withCtx(() => [
      _createVNode(_unref(GroupableTable), {
        "group-by": groupBy.value,
        "onUpdate:groupBy": _cache[6] || (_cache[6] = ($event: any) => ((groupBy).value = $event)),
        expanded: expanded.value,
        "onUpdate:expanded": _cache[7] || (_cache[7] = ($event: any) => ((expanded).value = $event)),
        expandable: "",
        columns: columns.value,
        rows: rows.value,
        "row-key": "id",
        "default-sort-by": "name",
        "default-group-by": groupBy.value,
        loading: loading.value,
        "filter-row": filterRow,
        "custom-cells": customCells,
        "data-cy": "view-artifact-table",
        onRowClick: handleView,
        "onGroup:open": handleOpenGroup,
        "onGroup:close": handleCloseGroup
      }, {
        "header-right": _withCtx(() => [
          _createVNode(_unref(MultiselectInput), {
            modelValue: visibleTypes.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((visibleTypes).value = $event)),
            outlined: "",
            dense: "",
            clearable: "",
            "use-chips": false,
            options: typeOptions.value,
            label: "Visible Types",
            b: "",
            class: "table-input q-mb-sm q-mr-sm",
            "data-cy": "input-trace-table-types"
          }, null, 8, ["modelValue", "options"]),
          _createVNode(_unref(SelectInput), {
            modelValue: countType.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((countType).value = $event)),
            outlined: "",
            dense: "",
            options: _unref(countOptions),
            "option-to-value": "",
            "option-label": "name",
            "option-value": "id",
            label: "Visible Artifacts",
            b: "",
            class: "table-input q-mb-sm q-mr-sm",
            "data-cy": "input-trace-table-count"
          }, null, 8, ["modelValue", "options"]),
          (inDeltaView.value)
            ? (_openBlock(), _createBlock(_unref(MultiselectInput), {
                key: 0,
                modelValue: deltaTypes.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((deltaTypes).value = $event)),
                outlined: "",
                dense: "",
                "use-chips": false,
                options: _unref(deltaOptions),
                label: "Delta Types",
                "option-to-value": "",
                "option-value": "id",
                "option-label": "name",
                class: "table-input q-mb-sm q-mr-sm",
                "data-cy": "input-delta-type",
                b: ""
              }, null, 8, ["modelValue", "options"]))
            : _createCommentVNode("", true),
          (!_unref(documentStore).isBaseDocument)
            ? (_openBlock(), _createBlock(_component_q_btn_group, {
                key: 1,
                flat: "",
                class: "q-mb-sm nav-mode-select"
              }, {
                default: _withCtx(() => [
                  _createVNode(TextButton, _mergeProps({ label: "Current View" }, buttonProps('view'), {
                    onClick: _cache[3] || (_cache[3] = ($event: any) => (showAll.value = false))
                  }), null, 16),
                  _createVNode(TextButton, _mergeProps({ label: "All Artifacts" }, buttonProps('all'), {
                    onClick: _cache[4] || (_cache[4] = ($event: any) => (showAll.value = true))
                  }), null, 16)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        "body-expanded": _withCtx(({ row }) => [
          _createVNode(_unref(ArtifactContentDisplay), { artifact: row }, null, 8, ["artifact"])
        ]),
        "body-cell-name": _withCtx(({ row }) => [
          _createVNode(_unref(ArtifactNameDisplay), {
            artifact: row,
            "display-tooltip": ""
          }, null, 8, ["artifact"])
        ]),
        "body-cell-type": _withCtx(({ row }: { row: FlatArtifact }) => [
          _createVNode(_unref(AttributeChip), {
            value: row.type,
            "artifact-type": ""
          }, null, 8, ["value"])
        ]),
        "body-cell-deltaType": _withCtx(({ row }) => [
          _createVNode(_unref(AttributeChip), {
            value: getDeltaType(row),
            "delta-type": ""
          }, null, 8, ["value"])
        ]),
        "body-cell-actions": _withCtx(({ row }) => [
          _createElementVNode("div", {
            onClick: _cache[5] || (_cache[5] = _withModifiers(() => {}, ["stop"]))
          }, [
            _createVNode(ArtifactTableRowActions, { artifact: row }, null, 8, ["artifact"])
          ])
        ]),
        _: 1
      }, 8, ["group-by", "expanded", "columns", "rows", "default-group-by", "loading"])
    ]),
    _: 1
  }))
}
}

})